import { Action } from 'store/store';

import { ChannelDestination } from 'communication/Channel';
import { NotifyableAction } from 'notification/NotificationState';
import {
    StreamError,
    StreamErrorType,
} from 'streamsState/store/streamStatuses/StreamErrorState';
import { NotificationType } from '@instinet-analytics/semantic-ui-components';

export enum StreamStatusActionType {
    STREAM_STATUS_SET_PENDING = 'STREAM_STATUS_SET_PENDING',
    STREAM_STATUS_SET_STREAMING = 'STREAM_STATUS_SET_STREAMING',
    STREAM_STATUS_SET_STOPPED = 'STREAM_STATUS_SET_STOPPED',
    STREAM_STATUS_HANDLE_STREAM_ERROR = 'STREAM_STATUS_HANDLE_STREAM_ERROR',
    STREAM_STATUS_HANDLE_ERROR = 'STREAM_STATUS_HANDLE_ERROR',
    STREAM_STATUS_REMOVE = 'STREAM_STATUS_REMOVE',
}

export interface StreamStatusActionPayload {
    streamKey: string;
}

export interface StreamStatusPendingActionPayload
    extends StreamStatusActionPayload {
    channelId: number;
    channelDestination?: ChannelDestination;
}

export type StreamStatusPendingAction = Action<
    StreamStatusActionType.STREAM_STATUS_SET_PENDING,
    StreamStatusPendingActionPayload
>;

export const createStreamStatusPendingAction: (
    streamKey: string,
    channelId: number,
    channelDestination?: ChannelDestination
) => StreamStatusPendingAction = (
    streamKey,
    channelId,
    channelDestination?
) => ({
    type: StreamStatusActionType.STREAM_STATUS_SET_PENDING,
    payload: { streamKey, channelId, channelDestination },
});

export type StreamStatusStreamingAction = Action<
    StreamStatusActionType.STREAM_STATUS_SET_STREAMING,
    StreamStatusActionPayload
>;

export const createStreamStatusStreamingAction: (
    streamKey: string
) => StreamStatusStreamingAction = (streamKey) => ({
    type: StreamStatusActionType.STREAM_STATUS_SET_STREAMING,
    payload: { streamKey },
});

export interface StreamStatusErrorActionPayload
    extends StreamStatusActionPayload {
    error: StreamError;
}

export type StreamStatusHandleStreamErrorAction = Action<
    StreamStatusActionType.STREAM_STATUS_HANDLE_STREAM_ERROR,
    StreamStatusErrorActionPayload
>;

export const createStreamStatusHandleStreamErrorAction: (
    streamKey: string,
    error: StreamError
) => StreamStatusHandleStreamErrorAction = (streamKey, error) => ({
    type: StreamStatusActionType.STREAM_STATUS_HANDLE_STREAM_ERROR,
    payload: { streamKey, error },
});

export type StreamStatusStoppedAction = Action<
    StreamStatusActionType.STREAM_STATUS_SET_STOPPED,
    { streamKey: string }
>;

export const createStreamStatusStoppedAction: (
    streamKey: string
) => StreamStatusStoppedAction = (streamKey) => ({
    type: StreamStatusActionType.STREAM_STATUS_SET_STOPPED,
    payload: { streamKey },
});

export type StreamStatusHandleErrorAction = Action<
    StreamStatusActionType.STREAM_STATUS_HANDLE_ERROR,
    { error: StreamError }
> &
    NotifyableAction;

export const createStreamStatusHandleErrorAction: (
    error: StreamError
) => StreamStatusHandleErrorAction = (error) => ({
    type: StreamStatusActionType.STREAM_STATUS_HANDLE_ERROR,
    payload: { error },
    notification: {
        message: getStreamErrorMessageText(error),
        type: NotificationType.Warning,
    },
});

export type StreamStatusRemoveAction = Action<
    StreamStatusActionType.STREAM_STATUS_REMOVE,
    StreamStatusActionPayload
>;

export const createStreamStatusRemoveAction: (
    streamKey: string
) => StreamStatusRemoveAction = (streamKey) => ({
    type: StreamStatusActionType.STREAM_STATUS_REMOVE,
    payload: { streamKey },
});

export type StreamStatusAction =
    | StreamStatusPendingAction
    | StreamStatusStreamingAction
    | StreamStatusHandleStreamErrorAction
    | StreamStatusStoppedAction
    | StreamStatusHandleErrorAction
    | StreamStatusRemoveAction;

export function getStreamErrorMessageText(error: StreamError) {
    return error.type === StreamErrorType.MALFORMED_MESSAGE_ERROR
        ? 'Malformed message received from server'
        : 'Unexpected error processing message';
}
